import { FaceDetector, FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import '../assets/index.css'
import * as ort from 'onnxruntime-web'
import NoMaskImagesModal from './modals/NoMaskImagesModal'
import MaskImagesModal from './modals/MaskImagesModal'
import ConsentModal from './modals/ConsentModal'
import CaptureConditionsModal from './modals/CaptureConditionsModal'
import { useUploadQueue } from '../hooks/useUploadQueue'

// ── Outside-component constants and pure functions ────────────────────────────

const MASKS_CLASSES = ['mask', 'no_mask']
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'

// Tâche 1 — Aligne un canvas cropé en utilisant l'angle inter-yeux
function alignFace(croppedCanvas, landmarks, originalWidth, originalHeight) {
    const SIZE = 224
    const lEye = landmarks[33]
    const rEye = landmarks[263]

    const lx = lEye.x * originalWidth
    const ly = lEye.y * originalHeight
    const rx = rEye.x * originalWidth
    const ry = rEye.y * originalHeight

    const angle = Math.atan2(ry - ly, rx - lx)

    const aligned = document.createElement('canvas')
    aligned.width = SIZE
    aligned.height = SIZE
    const ctx = aligned.getContext('2d')

    ctx.save()
    ctx.translate(SIZE / 2, SIZE / 2)
    ctx.rotate(-angle)
    ctx.translate(-SIZE / 2, -SIZE / 2)
    ctx.drawImage(croppedCanvas, 0, 0, SIZE, SIZE)
    ctx.restore()

    return ctx.getImageData(0, 0, SIZE, SIZE)
}

// Tâche 2 — Variance Laplacienne : retourne true si l'image est nette
function isSharp(imageData, threshold = 100) {
    const { data, width, height } = imageData
    const gray = new Float32Array(width * height)
    for (let i = 0; i < width * height; i++) {
        gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]
    }
    const kernel = [0, 1, 0, 1, -4, 1, 0, 1, 0]
    let variance = 0
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            let sum = 0
            for (let ky = -1; ky <= 1; ky++)
                for (let kx = -1; kx <= 1; kx++)
                    sum += gray[(y + ky) * width + (x + kx)] * kernel[(ky + 1) * 3 + (kx + 1)]
            variance += sum * sum
        }
    }
    return (variance / (width * height)) > threshold
}

// Tâche 3 — Retourne true si la frame courante est trop similaire à la précédente
function isDuplicate(prevData, currData, threshold = 0.02) {
    let diff = 0
    for (let i = 0; i < prevData.length; i += 4)
        diff += Math.abs(prevData[i] - currData[i])       // R
             + Math.abs(prevData[i + 1] - currData[i + 1]) // G
             + Math.abs(prevData[i + 2] - currData[i + 2]) // B
    return (diff / (prevData.length / 4 * 3 * 255)) < threshold
}

// ── Composant ─────────────────────────────────────────────────────────────────

const FaceRecognition = () => {
    const CAPTURE_INTERVAL = 1500
    const MAX_IMAGES_PER_STEP = 60

    const [isLoading, setIsLoading] = useState(false)
    const [isModelLoaded, setIsModelLoaded] = useState(false)
    const [error, setError] = useState('')
    const [detectedFaces, setDetectedFaces] = useState(null)
    const [isStreaming, setIsStreaming] = useState(false)
    const [maskOutput, setMaskOutput] = useState('')
    const [noMaskImages, setNoMaskImages] = useState([])
    const [maskImages, setMaskImages] = useState([])
    const [noMaskSessionEnded, setNoMaskSessionEnded] = useState(false)
    const [maskSessionEnded, setMaskSessionEnded] = useState(false)
    const [lastImage, setLastImage] = useState(null)
    const [step, setStep] = useState('no_mask')
    const [progress, setProgress] = useState(0)
    const [maskModelLoaded, setMaskModelLoaded] = useState(false)
    const [isImageSharp, setIsImageSharp] = useState(true)           // Tâche 2
    const [uploadProgress, setUploadProgress] = useState({ sent: 0, total: 0 }) // Tâche 5
    const [showConsentModal, setShowConsentModal] = useState(false)
    const [uploadDone, setUploadDone] = useState(false)
    const [uploadError, setUploadError] = useState(null)
    const [instructionsOpen, setInstructionsOpen] = useState(true)
    const [showStartModal, setShowStartModal] = useState(false)

    const faceDetectorRef = useRef(null)
    const faceLandmarkerRef = useRef(null)                            // Tâche 1
    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const lastFrameTimeRef = useRef(Date.now())
    const lastVideoTimeRef = useRef(-1)
    const animationFrameRef = useRef(null)
    const lastCaptureRef = useRef(0)
    const maskSessionRef = useRef(null)
    const isInferencingRef = useRef(false)
    const prevFrameDataRef = useRef(null)                             // Tâche 3
    const personIdRef = useRef(`person_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`)

    // Tâche 5 — Upload queue
    const { enqueue } = useUploadQueue(
        API_URL,
        () => { setUploadProgress(prev => ({ ...prev, sent: prev.sent + 1 })) },
        (message) => { setUploadError(message) }
    )

    useEffect(() => {
        if (uploadProgress.total > 0 && uploadProgress.sent === uploadProgress.total) {
            setUploadDone(true)
        }
    }, [uploadProgress])

    useEffect(() => { setInstructionsOpen(true) }, [step])

    useEffect(() => {
        if (!uploadDone) return
        fetch(`${API_URL}/notify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ personCode: personIdRef.current }),
        }).catch(() => {})
    }, [uploadDone])

    useEffect(() => {
        initializeFaceDetector()
        initializeFaceLandmarker()
        initializeMasksClassifier()

        return () => {
            if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
        }
    }, [])

    useEffect(() => {
        if (step === 'no_mask' && !noMaskSessionEnded && noMaskImages.length >= MAX_IMAGES_PER_STEP) {
            stopCamera()
        }
        if (step === 'mask' && !maskSessionEnded && maskImages.length >= MAX_IMAGES_PER_STEP) {
            stopCamera()
        }

        step === 'no_mask' && setProgress(((noMaskImages.length / MAX_IMAGES_PER_STEP) * 100).toFixed(2))
        step === 'mask' && setProgress(((maskImages.length / MAX_IMAGES_PER_STEP) * 100).toFixed(2))
    }, [noMaskSessionEnded, noMaskImages, maskImages, maskSessionEnded])

    const initializeFaceDetector = async () => {
        try {
            setIsLoading(true)
            setError('')

            const vision = await FilesetResolver.forVisionTasks("mediapipe/wasm")

            faceDetectorRef.current = await FaceDetector.createFromOptions(
                vision,
                {
                    baseOptions: {
                        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
                        delegate: "GPU",
                    },
                    runningMode: "VIDEO",
                    minDetectionConfidence: 0.8,
                }
            )

            setIsModelLoaded(true)
            console.log("Face Detector initialized successfully")
        } catch (err) {
            setError("Failled to initialize Face Detector")
            console.error("Face Detector Initializing error : ", err)
        } finally {
            setIsLoading(false)
        }
    }

    // Tâche 1 — Initialisation FaceLandmarker
    const initializeFaceLandmarker = async () => {
        try {
            const vision = await FilesetResolver.forVisionTasks("mediapipe/wasm")

            faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
                vision,
                {
                    baseOptions: {
                        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
                        delegate: "GPU",
                    },
                    runningMode: "VIDEO",
                    numFaces: 1,
                }
            )

            console.log("Face Landmarker initialized successfully")
        } catch (err) {
            console.error("Face Landmarker initialization failed:", err)
        }
    }

    const initializeMasksClassifier = async () => {
        try {
            if (maskSessionRef.current) return

            ort.env.wasm.wasmPaths = '/ort-wasm/'

            maskSessionRef.current = await ort.InferenceSession.create(
                "/classifiers/masks_classifier_v2.onnx",
                {
                    executionProviders: ["wasm"],
                    wasm: {
                        simd: true,
                        threads: 1,
                    }
                }
            )

            console.log("Masks classifier initialized successfullly")
            setMaskModelLoaded(true)
        } catch (err) {
            stopCamera()
            console.error("Masks classifier initialization failed : ", err)
        }
    }

    const detectFacesInVideo = useCallback(async () => {
        const video = videoRef.current
        const canvas = canvasRef.current
        const detector = faceDetectorRef.current

        if (!video || !canvas || !detector) {
            animationFrameRef.current = requestAnimationFrame(detectFacesInVideo)
            return
        }

        const vw = video.videoWidth
        const vh = video.videoHeight
        const cw = canvas.width
        const ch = canvas.height

        if (vw && vh && cw && ch && (cw !== vw || ch !== vh)) {
            canvas.height = vh
            canvas.width = vw
        }

        if (video.currentTime !== lastVideoTimeRef.current && !video.paused && !video.ended) {
            try {
                const results = detector.detectForVideo(video, performance.now())
                const ctx = canvas.getContext('2d')

                if (ctx) {
                    if (results && results.detections && results.detections.length > 0) {
                        setDetectedFaces(results.detections[0])
                        drawDetections(ctx, results.detections[0])
                        lastFrameTimeRef.current = performance.now()

                        const now = Date.now()
                        if (now - lastCaptureRef.current > CAPTURE_INTERVAL && !isInferencingRef.current) {
                            isInferencingRef.current = true

                            const imageData = captureFrame(results.detections[0].boundingBox)

                            // Tâche 2 — Rejet images floues
                            if (imageData && !isSharp(imageData)) {
                                console.log("Image rejetée : flou détecté")
                                setIsImageSharp(false)
                                isInferencingRef.current = false
                                // Ne pas mettre à jour lastCaptureRef
                            } else if (imageData) {
                                setIsImageSharp(true)

                                const infResult = await runMasksInference(imageData)

                                if (infResult) {
                                    // Tâche 4 — Seuil de confiance classificateur
                                    const confidence = Math.max(...infResult.probs)
                                    if (confidence < 0.85) {
                                        console.log("Prédiction ignorée : confiance trop faible")
                                    } else {
                                        const maskStatus = MASKS_CLASSES[infResult.index]
                                        setMaskOutput(maskStatus)

                                        if (step === 'no_mask' && maskStatus === 'no_mask')
                                            saveFaceFrame(imageData, (results.detections[0].categories[0].score * 100).toFixed(2), infResult.probs)

                                        if (step === 'mask' && maskStatus === 'mask')
                                            saveFaceFrame(imageData, (results.detections[0].categories[0].score * 100).toFixed(2), infResult.probs)
                                    }
                                }
                                isInferencingRef.current = false
                                lastCaptureRef.current = now
                            } else {
                                isInferencingRef.current = false
                                lastCaptureRef.current = now
                            }
                        }
                    } else {
                        setDetectedFaces(null)

                        if (canvas.width && canvas.height) {
                            ctx.clearRect(0, 0, canvas.width, canvas.height)
                        }

                        lastFrameTimeRef.current = performance.now()
                    }
                }
            } catch (err) {
                console.error("Face detection error : ", err)
            }

            lastFrameTimeRef.current = performance.now()
        }

        animationFrameRef.current = requestAnimationFrame(detectFacesInVideo)
    }, [detectedFaces, step])

    const drawDetections = (ctx, face) => {
        if (ctx.canvas.width && ctx.canvas.height) {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
        }

        const x = face.boundingBox.originX - 50
        const y = face.boundingBox.originY - 150
        const width = face.boundingBox.width + 100
        const height = face.boundingBox.height + 200

        ctx.strokeStyle = "#00ff88"
        ctx.lineWidth = 3
        ctx.strokeRect(x, y, width, height)

        ctx.fillStyle = "#00ff88"
        ctx.font = 'bold 16px Arial'
        ctx.fillText(`Face : ${(face.categories[0].score * 100).toFixed(2)}%`, x, y - 10)

        ctx.fillStyle = "#00ff88"
    }

    const startCamera = async () => {
        try {
            setIsLoading(true)
            setError('')

            if (!videoRef.current || !isModelLoaded) return

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 720 },
                    height: { ideal: 480 },
                    facingMode: 'user'
                }
            })

            if (videoRef.current) {
                videoRef.current.srcObject = stream
                videoRef.current.onloadedmetadata = async () => {
                    if (videoRef.current && canvasRef.current) {
                        const videoWidth = videoRef.current.videoWidth
                        const videoHeight = videoRef.current.videoHeight
                        if (videoWidth && videoHeight) {
                            canvasRef.current.width = videoWidth
                            canvasRef.current.height = videoHeight
                        }

                        setIsStreaming(true)

                        try {
                            if (faceDetectorRef.current) {
                                await faceDetectorRef.current.setOptions({ runningMode: "VIDEO" })
                            }
                        } catch (err) {
                            console.error("Failed to set video mode : ", err)
                        }

                        setTimeout(() => {
                            detectFacesInVideo()
                        }, 1500)
                    }
                }
            }
        } catch (err) {
            setError('Camera access denied')
            console.error("Camera error : ", err)
        } finally {
            setIsLoading(false)
        }
    }

    const stopCamera = () => {
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }

        if (videoRef.current?.srcObject) {
            const srcObject = videoRef.current.srcObject

            if (srcObject instanceof MediaStream) {
                srcObject.getTracks().forEach(track => track.stop())

                if (videoRef.current) {
                    videoRef.current.srcObject = null
                }
            }
        }

        setIsStreaming(false)

        step === 'no_mask' && setNoMaskSessionEnded(true)
        step === 'mask' && setMaskSessionEnded(true)

        setDetectedFaces(null)
        setLastImage(null)

        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d')

            if (ctx && canvasRef.current.width && canvasRef.current.height) {
                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
            }
        }
    }

    // Tâche 1 — Capture et aligne le visage en 224×224
    const captureFrame = (boundingBox) => {
        const video = videoRef.current
        if (!video || video.readyState !== 4) return null

        const SIZE = 224
        const x = boundingBox.originX - 50
        const y = boundingBox.originY - 150
        const width = boundingBox.width + 100
        const height = boundingBox.height + 200

        const canvas = document.createElement('canvas')
        canvas.width = SIZE
        canvas.height = SIZE
        const ctx = canvas.getContext('2d')
        ctx.drawImage(video, x, y, width, height, 0, 0, SIZE, SIZE)

        if (faceLandmarkerRef.current) {
            try {
                const landmarkResults = faceLandmarkerRef.current.detectForVideo(video, performance.now())
                if (landmarkResults?.faceLandmarks?.length > 0) {
                    return alignFace(canvas, landmarkResults.faceLandmarks[0], video.videoWidth, video.videoHeight)
                }
            } catch (err) {
                console.error("Face landmarker error:", err)
            }
        }

        return ctx.getImageData(0, 0, SIZE, SIZE)
    }

    // Tâche 1 — Normalisation ImageNet en 112×112 (était 224×224)
    const preProcessData = (imageData) => {
        if (!imageData || !imageData.data) return null

        const SIZE = 224
        const mean = [0.485, 0.456, 0.406]
        const std = [0.229, 0.224, 0.225]
        const data = new Float32Array(3 * SIZE * SIZE)

        let i = 0
        for (let y = 0; y < SIZE; y++) {
            for (let x = 0; x < SIZE; x++) {
                const idx = (y * SIZE + x) * 4
                data[i] = (imageData.data[idx] / 255 - mean[0]) / std[0]
                data[i + SIZE * SIZE] = (imageData.data[idx + 1] / 255 - mean[1]) / std[1]
                data[i + 2 * SIZE * SIZE] = (imageData.data[idx + 2] / 255 - mean[2]) / std[2]
                i++
            }
        }

        return data
    }

    const softmax = (arr) => {
        const max = Math.max(...arr)
        const exps = arr.map(x => Math.exp(x - max))
        const sum = exps.reduce((a, b) => (a + b))
        return exps.map(x => x / sum)
    }

    const postProcessData = (logits) => {
        const probs = softmax(logits)
        return { probs: probs, index: probs.indexOf(Math.max(...probs)) }
    }

    // Tâche 1 — Accepte imageData directement (capture unique dans detectFacesInVideo)
    const runMasksInference = async (imageData) => {
        try {
            if (!maskSessionRef.current) return null

            const input = preProcessData(imageData)
            if (!input) return null

            // Tâche 1 — Shape 112×112 au lieu de 224×224
            const tensor = new ort.Tensor("float32", input, [1, 3, 224, 224])
            const feeds = { [maskSessionRef.current.inputNames[0]]: tensor }
            const results = await maskSessionRef.current.run(feeds)
            const output = results[maskSessionRef.current.outputNames[0]].data

            return postProcessData(output)
        } catch (err) {
            console.error("Mask inference error:", err)
            return null
        }
    }

    // Tâche 3 — Déduplication + reçoit imageData directement (plus de double-capture)
    const saveFaceFrame = (imageData, faceConf, maskProbs) => {
        if (step === 'no_mask' && noMaskImages.length >= MAX_IMAGES_PER_STEP) return
        if (step === 'mask' && maskImages.length >= MAX_IMAGES_PER_STEP) return

        // Tâche 3 — Vérification de duplication
        if (prevFrameDataRef.current && isDuplicate(prevFrameDataRef.current, imageData.data)) return

        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        canvas.width = 224
        canvas.height = 224
        ctx.putImageData(imageData, 0, 0)

        prevFrameDataRef.current = imageData.data

        canvas.toBlob((blob) => {
            const url = canvas.toDataURL()
            setLastImage({ blob, url, maskProbs })

            step === 'no_mask' && setNoMaskImages(prev => {
                if (prev.length >= MAX_IMAGES_PER_STEP) return prev
                return [...prev, { blob, url, faceConf, maskProbs }]
            })

            step === 'mask' && setMaskImages(prev => {
                if (prev.length >= MAX_IMAGES_PER_STEP) return prev
                return [...prev, { blob, url, faceConf, maskProbs }]
            })
        }, "image/png", 0.95)
    }

    // Tâche 5 — Ouvre le modal de consentement avant l'upload
    const handleSaveAll = () => {
        setShowConsentModal(true)
    }

    const handleConsentConfirm = (personInfo) => {
        setShowConsentModal(false)
        const sessionId = Date.now().toString()
        const total = noMaskImages.length + maskImages.length
        setUploadProgress({ sent: 0, total })

        noMaskImages.forEach(img => enqueue(img.blob, {
            personId: personIdRef.current,
            isMasked: false,
            timestamp: Date.now(),
            sessionId,
            personName:    personInfo.name,
            personEmail:   personInfo.email,
            personContact: personInfo.contact,
            faceConf: img.faceConf,
            maskProbs: img.maskProbs
        }))

        maskImages.forEach(img => enqueue(img.blob, {
            personId: personIdRef.current,
            isMasked: true,
            timestamp: Date.now(),
            sessionId,
            personName:    personInfo.name,
            personEmail:   personInfo.email,
            personContact: personInfo.contact,
            faceConf: img.faceConf,
            maskProbs: img.maskProbs
        }))
    }

    const nextStep = () => {
        setStep('mask')
        setLastImage(null)
    }

    const handleNewSession = () => {
        setUploadDone(false)
        setNoMaskImages([])
        setMaskImages([])
        setNoMaskSessionEnded(false)
        setMaskSessionEnded(false)
        setLastImage(null)
        setStep('no_mask')
        setProgress(0)
        setMaskOutput('')
        setUploadProgress({ sent: 0, total: 0 })
        setUploadError(null)
        prevFrameDataRef.current = null
        personIdRef.current = `person_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    }

    const startOverMaskStep = () => {
        setMaskSessionEnded(false)
        setStep('mask')
        setLastImage(null)
        setMaskImages([])
    }

    const startOverNoMaskStep = () => {
        setNoMaskSessionEnded(false)
        setStep('no_mask')
        setLastImage(null)
        setNoMaskImages([])
    }

  return (
    <div className='row m-0' style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0d1b2a 0%, #1b2d45 60%'}}>
        <nav className="navbar px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <span className="navbar-brand text-white fw-bold fs-5 d-flex align-items-center gap-2">
                <span style={{ fontSize: '1.4rem' }}>🎭</span>
                Awesome Faces
            </span>
            <span className="badge bg-primary">Dataset v1.0</span>
        </nav>
        <div className="col-12 py-5">
            <div className="card shadow mx-auto face-card" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}>
                <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'white' }}>
                    <strong>Face Dataset Collector</strong>
                    {isStreaming ? <span className="badge bg-success">En marche</span> : <span className="badge bg-warning">En arrêt</span>}
                </div>

                {uploadDone ? (
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-success mb-3" viewBox="0 0 16 16">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                            </svg>
                            <h4 className="fw-bold">Merci pour votre participation !</h4>
                            <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                                Vos {uploadProgress.total} images ont bien été enregistrées dans le dataset.<br />
                                Votre contribution est précieuse pour l'entraînement du modèle.
                            </p>
                        </div>
                        <button type="button" className="btn btn-primary" onClick={handleNewSession}>
                            Nouvelle session
                        </button>
                    </div>
                ) : (
                    <>
                        {isStreaming &&
                            <div className="row text-center mb-3 g-2 mt-3">
                                <div className="col-auto mx-auto">
                                    {detectedFaces !== null
                                        ? <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>✓ Visage détecté</span>
                                        : <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.35)', fontSize: '0.75rem' }}>Visage non détecté</span>
                                    }
                                </div>
                                <div className="col-auto mx-auto">
                                    {isImageSharp
                                        ? <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>🔆 Image nette</span>
                                        : <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(234,134,143,0.15)', color: '#ea868f', border: '1px solid rgba(234,134,143,0.35)', fontSize: '0.75rem' }}>🌫 Image floue</span>
                                    }
                                </div>
                                {lastImage !== null &&
                                    <div className="col-auto mx-auto">
                                        {(step === 'no_mask' && maskOutput === 'no_mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>🙂 Sans masque : {(lastImage.maskProbs[1] * 100).toFixed(1)}%</span>}
                                        {(step === 'no_mask' && maskOutput === 'mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.35)', fontSize: '0.75rem' }}>😷 Masque détecté : {(lastImage.maskProbs[0] * 100).toFixed(1)}%</span>}
                                        {(step === 'mask' && maskOutput === 'mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>😷 Masque : {(lastImage.maskProbs[0] * 100).toFixed(1)}%</span>}
                                        {(step === 'mask' && maskOutput === 'no_mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.35)', fontSize: '0.75rem' }}>🙂 Sans masque : {(lastImage.maskProbs[1] * 100).toFixed(1)}%</span>}
                                    </div>
                                }
                                <div className="card-body">
                                    <h5 className="card-title text-center">Images collectées</h5>
                                    <p className="card-text text-center">
                                        {step === 'no_mask' && <div className="image-counter">{noMaskImages.length}/{MAX_IMAGES_PER_STEP}</div>}
                                        {step === 'mask' && <div className="image-counter">{maskImages.length}/{MAX_IMAGES_PER_STEP}</div>}
                                    </p>
                                    <div className="progress" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                            role="progressbar"
                                            style={{ width: `${progress}%` }}
                                            aria-valuenow={progress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        >{`${progress}%`}</div>
                                    </div>
                                </div>
                            </div>
                        }
                        {(!isStreaming) &&
                            <div className="camera-wrapper"
                                style={
                                    {
                                        display : (noMaskSessionEnded && maskSessionEnded) ? 'none' : 'block'
                                    }
                                }
                            >
                                <div className="camera-placeholder d-flex flex-column align-items-center justify-content-center">
                                    {(!isModelLoaded || !maskModelLoaded) &&
                                        <div className='px-5' style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
                                            <span className="badge d-block rounded-pill px-3 py-2 mb-3" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.3)' }}>Chargement des modèles…</span>
                                            <span className='d-block mb-2' style={{fontSize: '16px'}}>Veuillez attendre jusqu'à ce que les modèles se chargent.</span>
                                            <span className='d-block mb-2' style={{fontSize: '16px'}}>Les instructions à suivre apparaitront une fois le chargement terminé.</span>
                                            <span className="badge d-block rounded-pill px-3 py-2 mt-3" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.3)' }}>NB : Cela peut prendre un peu de temps</span>
                                        </div>
                                    }
                                    
                                    {(isModelLoaded && maskModelLoaded) &&
                                        <div>
                                            {/* Instructions déroulantes — conditions de capture */}
                                            {!uploadDone && !(step === 'no_mask' ? noMaskSessionEnded : maskSessionEnded) && (
                                                <div className="mb-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setInstructionsOpen(o => !o)}
                                                        className="d-flex align-items-center gap-2 w-100 text-start border-0 px-3 py-2 rounded-3"
                                                        style={{ background: 'rgba(255,255,255,0.04)', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}
                                                    >
                                                        <span>{step === 'no_mask' ? '🙂' : '😷'}</span>
                                                        <span className="fw-semibold flex-grow-1">
                                                            {step === 'no_mask' ? 'Étape 1 — Capture sans masque' : 'Étape 2 — Capture avec masque'}
                                                        </span>
                                                        <span style={{ fontSize: '0.65rem', opacity: 0.5 }}>{instructionsOpen ? '▲' : '▼'} Consignes</span>
                                                    </button>
                                                    {instructionsOpen && (
                                                        <div className="px-3 pt-2 pb-3 rounded-bottom-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderTop: 'none' }}>
                                                            {step === 'no_mask' ? (
                                                                <ul className="mb-0" style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', paddingLeft: '1.2rem', lineHeight: 1.8 }}>
                                                                    <li>Retirez tout masque, couvre-bouche ou protection faciale</li>
                                                                    <li>Placez-vous face à la caméra, à environ 50 cm</li>
                                                                    <li>Éclairage homogène recommandé — évitez les contre-jours</li>
                                                                    <li>Gardez une expression neutre et variez légèrement l'orientation de la tête</li>
                                                                    <li>{MAX_IMAGES_PER_STEP} photos seront prises automatiquement</li>
                                                                </ul>
                                                            ) : (
                                                                <ul className="mb-0" style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', paddingLeft: '1.2rem', lineHeight: 1.8 }}>
                                                                    <li>Portez un masque chirurgical ou tout autre type de cache-nez</li>
                                                                    <li>Même distance et conditions d'éclairage que l'étape précédente</li>
                                                                    <li>Assurez-vous que le masque est bien ajusté et couvre entièrement nez et bouche</li>
                                                                    <li>Variez légèrement l'orientation de la tête comme à l'étape précédente</li>
                                                                    <li>{MAX_IMAGES_PER_STEP} photos seront prises automatiquement</li>
                                                                </ul>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {
                                                ((step === 'no_mask' && !noMaskSessionEnded && isModelLoaded && maskModelLoaded) || (step === 'mask' && !maskSessionEnded && isModelLoaded && maskModelLoaded)) &&
                                                <div className='text-center mt-3'>
                                                    <button
                                                        onClick={() => setShowStartModal(true)}
                                                        className="btn btn-primary btn-lg px-5 py-3 fw-semibold shadow"
                                                        style={{ borderRadius: 12, fontSize: '1rem' }}
                                                        disabled={isStreaming}
                                                    >
                                                        Débuter
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                    }

                                    {
                                        noMaskSessionEnded && step === 'no_mask' &&
                                        <>
                                            <div style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
                                                <span className="d-block mb-2" style={{fontSize: '16px'}}>Fin de l'étape de capture sans masque.</span>
                                                <span className="d-block mb-2" style={{fontSize: '16px'}}>Cliquez sur le bouton dédiée pour jetter un oeil aux images enregistrées.</span>
                                                <span className="d-block mb-2" style={{fontSize: '16px'}}>Vous pouvez passer à l'étape suivante.</span>
                                            </div>
                                            <div className='mt-3 d-flex flex-wrap justify-content-center gap-3'>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-lg px-5 py-3 fw-semibold shadow" 
                                                    style={{ borderRadius: 12, fontSize: '1rem' }}
                                                    onClick={nextStep}
                                                >
                                                    Etape suivante
                                                </button>
                                                <button 
                                                    type="button" 
                                                    data-bs-toggle="modal" 
                                                    data-bs-target="#no_mask_images_modals" 
                                                    class="btn btn-lg px-5 py-3 fw-semibold" 
                                                    style={
                                                        {
                                                            borderRadius : '12px',
                                                            background: 'rgba(255, 255, 255, 0.08)',
                                                            color : 'rgba(255, 255, 255, 0.8)',
                                                            border : '1px solid rgba(255, 255, 255, 0.15)',
                                                            fontSize : '1rem'

                                                        }
                                                    }
                                                >Voir les images sans masque</button>
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>
                        }
                        <div className='video-wrapper'
                            style={{ display: isStreaming ? 'block' : 'none' }}
                        >
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-100 rounded"
                            />
                            <canvas
                                ref={canvasRef}
                                className='w-100 h-100'
                            />
                        </div>

                        <div className="card-body">
                            {isStreaming &&
                                <div className="row text-center mb-3 g-2">
                                    <div className="col-auto mx-auto">
                                        {detectedFaces !== null
                                            ? <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>✓ Visage détecté</span>
                                            : <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.35)', fontSize: '0.75rem' }}>Visage non détecté</span>
                                        }
                                    </div>
                                    <div className="col-auto mx-auto">
                                        {isImageSharp
                                            ? <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>🔆 Image nette</span>
                                            : <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(234,134,143,0.15)', color: '#ea868f', border: '1px solid rgba(234,134,143,0.35)', fontSize: '0.75rem' }}>🌫 Image floue</span>
                                        }
                                    </div>
                                    {lastImage !== null &&
                                        <div className="col-auto mx-auto">
                                            {(step === 'no_mask' && maskOutput === 'no_mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>🙂 Sans masque : {(lastImage.maskProbs[1] * 100).toFixed(1)}%</span>}
                                            {(step === 'no_mask' && maskOutput === 'mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.35)', fontSize: '0.75rem' }}>😷 Masque détecté : {(lastImage.maskProbs[0] * 100).toFixed(1)}%</span>}
                                            {(step === 'mask' && maskOutput === 'mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(117,183,152,0.15)', color: '#75b798', border: '1px solid rgba(117,183,152,0.35)', fontSize: '0.75rem' }}>😷 Masque : {(lastImage.maskProbs[0] * 100).toFixed(1)}%</span>}
                                            {(step === 'mask' && maskOutput === 'no_mask') && <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(255,193,7,0.15)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.35)', fontSize: '0.75rem' }}>🙂 Sans masque : {(lastImage.maskProbs[1] * 100).toFixed(1)}%</span>}
                                        </div>
                                    }
                                </div>
                            }

                            {isStreaming &&
                                <div className="col-md d-none d-md-block">
                                    <div className="card mb-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}>
                                        <div className="row g-0 align-items-center">
                                            <div className={lastImage !== null ? 'col-md-8' : 'col-md-12'}>
                                                <div className="card-body">
                                                    <h5 className="card-title text-center">Images collectées</h5>
                                                    <p className="card-text text-center">
                                                        {step === 'no_mask' && <div className="image-counter">{noMaskImages.length}/{MAX_IMAGES_PER_STEP}</div>}
                                                        {step === 'mask' && <div className="image-counter">{maskImages.length}/{MAX_IMAGES_PER_STEP}</div>}
                                                    </p>
                                                    <div className="progress" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                                        <div
                                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                                            role="progressbar"
                                                            style={{ width: `${progress}%` }}
                                                            aria-valuenow={progress}
                                                            aria-valuemin="0"
                                                            aria-valuemax="100"
                                                        >{`${progress}%`}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            {lastImage !== null &&
                                                <div className="col-md-4">
                                                    <img className="card-img card-img-right" src={lastImage.url} alt="Card image" />
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>

                        {(noMaskSessionEnded && maskSessionEnded) &&
                            <>
                                <h2 className="text-white fw-bold text-center mb-2" style={{ fontSize: '1.5rem' }}>Fin de la session</h2>
                                <p className="text-center mb-3" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                                    Cliquer sur <b>Tout enregistrer</b> pour sauvegarder vos images. <br />
                                </p>
                                <p className="text-center mb-3" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                                    Cliquer sur <b>Voir</b> pour visualiser vos images.<br />
                                </p>
                                <div className="row g-3 mb-3 px-3">
                                    <div className="col-6">
                                        <div className="p-3 rounded-3 text-center h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                            <div className="fw-bold mb-1" style={{ fontSize: '2rem', color : '#75b798', lineHeight: 1.1 }}>{maskImages.length}</div>
                                            <div className="text-white fw-semibold" style={{ fontSize: '0.82rem' }}>Avec masque</div>
                                            <span 
                                                className="badge bg-success mt-2"
                                                data-bs-toggle="modal"
                                                data-bs-target="#mask_images_modals"
                                            >Voir</span>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-3 rounded-3 text-center h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                            <div className="fw-bold mb-1" style={{ fontSize: '2rem', color : '#ea868f', lineHeight: 1.1 }}>{noMaskImages.length}</div>
                                            <div className="text-white fw-semibold" style={{ fontSize: '0.82rem' }}>Sans masque</div>
                                            <span 
                                                className="badge bg-danger mt-2"
                                                data-bs-toggle="modal"
                                                data-bs-target="#no_mask_images_modals"
                                            >Voir</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer text-center" style={{ background: 'rgba(255,255,255,0.04)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                    <div className='mb-3'>
                                        {/* Tâche 5 — Progression upload */}
                                        {uploadError &&
                                            <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(234,134,143,0.15)', color: '#ea868f', border: '1px solid rgba(234,134,143,0.35)', fontSize: '0.78rem' }}>
                                                {uploadError}
                                            </span>
                                        }
                                        {!uploadError && uploadProgress.total > 0 &&
                                            <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(13,110,253,0.2)', color: '#6ea8fe', border: '1px solid rgba(13,110,253,0.4)', fontSize: '0.78rem' }}>
                                                ☁️ Envoyées : {uploadProgress.sent} / {uploadProgress.total}
                                            </span>
                                        }
                                    </div>
                                    
                                    {/* Tâche 5 — Progression upload */}
                                    <div className='d-flex justify-content-evenly align-items-center flex-wrap gap-3'>
                                            <button 
                                            type='button' 
                                            className="btn btn-primary btn-lg px-5 py-3 fw-semibold shadow" 
                                            style={{ borderRadius: 12, fontSize: '1rem' }}
                                            onClick={handleSaveAll} 
                                            disabled={uploadProgress.total > 0 && !uploadError}
                                        >Tout enregistrer</button>
                                        <button 
                                            type="button" 
                                            className="btn btn-warning btn-lg px-5 py-3 fw-semibold shadow" style={{ borderRadius: 12, fontSize: '1rem' }}
                                            onClick={() => { startOverMaskStep(); startOverNoMaskStep() }}
                                        >Tout recommencer</button>
                                    </div>
                                </div>
                            </>
                        }
                    </>
                )}
            </div>
        </div>

        <NoMaskImagesModal images={noMaskImages} startOverNoMaskStep={startOverNoMaskStep}/>
        <MaskImagesModal images={maskImages} startOverMaskStep={startOverMaskStep}/>
        <CaptureConditionsModal
            show={showStartModal}
            step={step}
            maxImages={MAX_IMAGES_PER_STEP}
            onStart={() => { setShowStartModal(false); startCamera() }}
            onClose={() => setShowStartModal(false)}
        />
        <ConsentModal
            show={showConsentModal}
            onConfirm={handleConsentConfirm}
            onClose={() => setShowConsentModal(false)}
        />
        {/* Footer */}
        <footer className="text-center py-4 px-3" style={{ background: 'rgba(0,0,0,0.35)', borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>
            Projet de soutenance &mdash; Master Data Science &bull; Les données collectées sont destinées exclusivement à un usage académique.
        </footer>
    </div>
  )
}

export default FaceRecognition
